"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useLocale } from "next-intl";
import { AnimatePresence, motion } from "motion/react";
import { useRouter, usePathname } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";

const COOKIE = "NEXT_LOCALE";

type SupportedLocale = (typeof routing.locales)[number];

const config: Record<SupportedLocale, {
  flag: string;
  name: string;
  prompt: string;
  switchLabel: string;
  dismissLabel: string;
}> = {
  en: {
    flag: "/assets/flags/gb.svg",
    name: "English",
    prompt: "View this site in English?",
    switchLabel: "Switch",
    dismissLabel: "Dismiss",
  },
  de: {
    flag: "/assets/flags/de.svg",
    name: "Deutsch",
    prompt: "Diese Seite auf Deutsch anzeigen?",
    switchLabel: "Wechseln",
    dismissLabel: "Schließen",
  },
  no: {
    flag: "/assets/flags/no.svg",
    name: "Norsk",
    prompt: "Vis denne siden på norsk?",
    switchLabel: "Bytt",
    dismissLabel: "Avvis",
  },
};

function hasCookie(name: string) {
  return document.cookie.split("; ").some((c) => c.startsWith(`${name}=`));
}

function setCookie(name: string, value: string) {
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${name}=${value}; path=/; max-age=${oneYear}; samesite=lax`;
}

async function detectSuggested(current: SupportedLocale): Promise<SupportedLocale | null> {
  try {
    const override = new URLSearchParams(window.location.search).get("geo");
    const res = await fetch(
      override ? `/api/geo?country=${encodeURIComponent(override)}` : "/api/geo",
      { cache: "no-store" },
    );
    if (res.ok) {
      const data = (await res.json()) as { locale?: string };
      const suggested = data.locale;
      if (
        suggested &&
        suggested !== current &&
        (routing.locales as readonly string[]).includes(suggested)
      ) {
        return suggested as SupportedLocale;
      }
    }
  } catch {
    // fall through to browser language
  }

  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const raw of langs) {
    const short = raw.toLowerCase().slice(0, 2);
    const mapped: string = short === "nb" || short === "nn" ? "no" : short;
    if (mapped === current) return null;
    if ((routing.locales as readonly string[]).includes(mapped)) {
      return mapped as SupportedLocale;
    }
  }
  return null;
}

const LocaleSuggestionBanner = () => {
  const current = useLocale() as SupportedLocale;
  const router = useRouter();
  const pathname = usePathname();
  const [suggested, setSuggested] = useState<SupportedLocale | null>(null);

  useEffect(() => {
    if (hasCookie(COOKIE)) return;
    let cancelled = false;
    detectSuggested(current).then((result) => {
      if (!cancelled) setSuggested(result);
    });
    return () => {
      cancelled = true;
    };
  }, [current]);

  if (!suggested) return null;
  const s = config[suggested];

  const accept = () => {
    setCookie(COOKIE, suggested);
    router.replace(pathname, { locale: suggested });
  };

  const dismiss = () => {
    setCookie(COOKIE, current);
    setSuggested(null);
  };

  return (
    <AnimatePresence>
      <motion.div
        role="region"
        aria-label={s.prompt}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 inset-x-0 z-[60] flex justify-center px-3 pt-[22px] pointer-events-none"
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0A001F] via-[#0A001F]/70 to-transparent"
        />
        <div className="relative pointer-events-auto flex items-center gap-3 px-3 py-2 rounded-full bg-[#121124]/90 border border-white/[0.08] backdrop-blur-xl shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
          <span className="relative w-[22px] h-[22px] rounded-full overflow-hidden flex-shrink-0">
            <Image
              src={s.flag}
              alt={s.name}
              aria-hidden="true"
              width={44}
              height={33}
              className="absolute inset-0 w-full h-full object-cover scale-[1.12]"
            />
          </span>
          <span className="text-[13px] text-white/80 font-medium">{s.prompt}</span>
          <button
            onClick={accept}
            className="text-[13px] font-semibold text-white px-3 py-1 rounded-full bg-white/[0.08] hover:bg-white/[0.14] transition-colors cursor-pointer"
          >
            {s.switchLabel}
          </button>
          <button
            onClick={dismiss}
            aria-label={s.dismissLabel}
            className="w-6 h-6 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/[0.08] transition-colors cursor-pointer"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LocaleSuggestionBanner;
